import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/contexts/LangContext";

const ease = [0.22, 1, 0.36, 1];

export default function Process() {
  const { t } = useLang();
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });
  const steps = t.process.steps;

  return (
    <section id="process" data-testid="process-section" className="relative py-28 lg:py-36">
      <div className="absolute inset-0 bg-radial-blue opacity-40 pointer-events-none" />
      <div className="mx-auto max-w-7xl px-5 relative">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease }}
          className="max-w-3xl mb-16"
        >
          <div className="text-[11px] sm:text-xs uppercase tracking-[0.24em] text-brand-orange font-mono-tight mb-4">
            {t.process.eyebrow}
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[1.02]">
            {t.process.title} <br />
            <span className="text-gradient-brand">{t.process.title2}</span>
          </h2>
        </motion.div>

        {/* Stacked cards — each step sticks and the next stacks on top */}
        <div ref={containerRef} className="relative">
          {steps.map((s, i) => (
            <StackCard
              key={s.n}
              step={s}
              index={i}
              total={steps.length}
              progress={scrollYProgress}
              phase={t.process.phase}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function StackCard({ step, index, total, progress, phase }) {
  const targetScale = 1 - (total - 1 - index) * 0.05;
  const scale = useTransform(progress, [index / total, 1], [1, targetScale]);
  const isOrange = index % 2 === 1;

  return (
    <div
      className="sticky mb-6 lg:mb-8"
      style={{ top: `calc(100px + ${index * 24}px)`, zIndex: index + 1 }}
      data-testid={`process-step-${index}`}
    >
      <motion.div
        style={{ scale, transformOrigin: "top center" }}
        className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-[#0e1729] p-7 sm:p-10 lg:p-12 min-h-[300px] sm:min-h-[330px] shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)]"
      >
        {/* accent glow */}
        <div
          className={`absolute -top-28 -right-28 h-80 w-80 rounded-full blur-3xl pointer-events-none ${
            isOrange ? "bg-brand-orange/10" : "bg-brand-blue/12"
          }`}
        />
        <div className="absolute inset-0 grid-bg opacity-[0.15] pointer-events-none" />

        <div className="relative grid md:grid-cols-[auto_1fr] gap-6 lg:gap-14 items-start">
          <div
            className={`font-display text-6xl sm:text-7xl lg:text-[110px] font-black tracking-tighter leading-none ${
              isOrange ? "text-gradient-orange" : "text-gradient-blue"
            }`}
          >
            {step.n}
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <span
                className={`h-1.5 w-1.5 rounded-full animate-pulse ${
                  isOrange ? "bg-brand-orange" : "bg-brand-blue"
                }`}
              />
              <span className="text-[11px] sm:text-xs font-mono-tight uppercase tracking-[0.2em] text-white/55">
                {phase} {step.n} / 0{total}
              </span>
            </div>

            <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight leading-tight">
              {step.title}
            </h3>
            <p className="mt-4 text-[15px] sm:text-base lg:text-lg text-white/60 leading-relaxed max-w-2xl">
              {step.desc}
            </p>

            {/* step progress */}
            <div className="mt-8 flex gap-1.5" aria-hidden="true">
              {Array.from({ length: total }).map((_, d) => {
                const reached = d <= index;
                const activeColor = isOrange ? "bg-brand-orange w-8" : "bg-brand-blue w-8";
                return (
                  <span
                    key={`dot-${d}`}
                    className={`h-1 rounded-full transition-all ${reached ? activeColor : "bg-white/10 w-4"}`}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
